export interface ChatSession {
  id: string;
  userId: string;
  title: string | null;
  createdAt: string;
}

export interface ChatMessage {
  id: string;
  sessionId: string;
  role: "user" | "assistant" | "tool";
  content: string;
  sql: string | null;
  resultRef: string | null;
  bytesBilled: number | null;
  createdAt: string;
}

export interface ChartSpec {
  type: "bar" | "line" | "pie";
  x: string;
  y: string;
  series?: string;
}

export interface SendMessageResponse {
  message: ChatMessage;
  rows: Record<string, unknown>[] | null;
  chart: ChartSpec | null;
  trace: Array<{ kind: "sql" | "error"; content: string }>;
}

async function req<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const res = await fetch(input, { ...init, credentials: "include" });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status}: ${body}`);
  }
  return (await res.json()) as T;
}

export async function listSessions(): Promise<{ sessions: ChatSession[] }> {
  return req("/api/chat/sessions");
}

export async function createSession(title?: string): Promise<{ session: ChatSession }> {
  return req("/api/chat/sessions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title }),
  });
}

export async function getSession(
  id: string,
): Promise<{ session: ChatSession; messages: ChatMessage[] }> {
  return req(`/api/chat/sessions/${id}`);
}

export async function sendMessage(
  sessionId: string,
  content: string,
): Promise<SendMessageResponse> {
  return req(`/api/chat/sessions/${sessionId}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content }),
  });
}

export async function saveAsReport(args: {
  name: string;
  description?: string;
  sql: string;
  chartConfig?: ChartSpec;
}): Promise<{ report: { id: string } }> {
  return req("/api/reports", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...args, source: "chat" }),
  });
}
